import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import LazyLoadBackend from "./services/core/i18nBackend";
import { API_BASE_URL } from "./services/common/constants";
import { logger } from "./utils/logger";

logger.log("[i18n] Initializing with API_BASE_URL:", API_BASE_URL);

i18n
  .use(LazyLoadBackend) // Use our custom backend for lazy loading
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: [
      "en", "en-US",
      "pl", "pl-PL",
      "de", "de-DE",
      "es", "es-ES",
      "fr", "fr-FR",
      "ja", "ja-JP",
      "zh", "zh-CN",
      "uk", "uk-UA"
    ],
    fallbackLng: "en-US",
    debug: false, // Temporarily disabled to reduce noise from race conditions
    nonExplicitSupportedLngs: true, // This allows 'pl' to match 'pl-*'

    ns: ["translation"], // Use default namespace for now
    defaultNS: "translation",

    interpolation: {
      escapeValue: false,
    },

    backend: {
      loadPath: `${API_BASE_URL}/api/translations/{{lng}}`, // Used by custom backend for initial load
    },

    react: {
      useSuspense: false,
      bindI18n: "languageChanged loaded", // Re-render on language change and resource load
      bindI18nStore: "added removed", // Re-render when resources are added/removed
    },

    detection: {
      // Configure language detection
      order: ["localStorage", "navigator"], // Check localStorage first, then browser settings
      lookupLocalStorage: "i18nextLng", // Use default i18next key for the entire app
      caches: ["localStorage"], // Cache the language in localStorage
      excludeCacheFor: ["cimode"], // Languages to not cache
      // checkForSimilarInWhitelist replaced deprecated checkWhitelist
      convertDetectedLanguage: (lng: string) => {
        // Convert short codes to full codes
        const codeMap: Record<string, string> = {
          "en": "en-US",
          "pl": "pl-PL",
          "de": "de-DE",
          "es": "es-ES",
          "fr": "fr-FR",
          "ja": "ja-JP",
          "zh": "zh-CN",
          "uk": "uk-UA"
        };
        return codeMap[lng] || lng;
      },
    },
  });

// Add event listeners to debug
i18n.on("initialized", (options) => {
  logger.log("[i18n] Initialized with options:", options);
});

i18n.on("loaded", (loaded) => {
  logger.log("[i18n] Loaded namespaces:", loaded);
});

i18n.on("failedLoading", (lng, ns, msg) => {
  logger.error("[i18n] Failed loading:", { lng, ns, msg });
});

i18n.on("languageChanged", async (lng) => {
  logger.log("[i18n] Language changed to:", lng);

  // Import translation service dynamically to avoid circular dependencies
  const { translationService } = await import(
    "./services/core/translationService"
  );

  // Clear the loaded namespaces tracking so they reload for the new language
  translationService.clearLoadedNamespaces();

  // Clear the cache for the new language to ensure fresh translations
  logger.log("[i18n] Clearing cache for language:", lng);
  translationService.clearCacheForLanguage(lng);

  // Note: Don't call removeResourceBundle here - it causes a race condition where
  // RouteAwareTranslationProvider loads translations and then this handler removes them.
  // clearLoadedNamespaces() already ensures translations will be reloaded by the provider.

  // Don't reload namespaces here - let AdminLayout and GuestRouteGuard handle it
  // This prevents race conditions where translations get loaded multiple times
  logger.log(
    "[i18n] Language change complete, components will reload namespaces",
  );
});

export default i18n;
